import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { ServicesURL, arrayInString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ButtonBackPage from "@components/ButtonBackPage";
import { ColumnsType } from "src/interfaces/Common";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useFilters } from "@context/filters";
import { useAuth } from "@context/auth";

const CoursesDigited: React.FC = () => {
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["catalog.courses"]}`;
  const [idToDelete, setIdToDelete] = useState(undefined);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  //GET
  const { columns, dataTable, actionsTopToolbar, actionForRow } = useFetchTable(
    `${ServicesURL["learning-catalogue"]}/1/learning-objects/courses`,
    PagesID["catalog.courses"],
    filters[filterId],
  );
  //TRANSLATION
  const { t } = useTranslation(["courses-digited-page", "modal"]);

  //DELETE
  const handleDelete = React.useCallback(async () => {
    setOpenToast({ s: StatusEnum.KO, message: "WIP: DELETE" });
  }, []);

  const columnsMerged =
    columns?.map((el: ColumnsType) => {
      switch (el.accessorKey) {
        case "corporate_visibility": {
          return {
            ...el,
            Cell: ({ cell }: any) => {
              const value = cell.getValue();
              return arrayInString(value, "label");
            },
          };
        }
        default:
          return el;
      }
    }) || [];

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-courses.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={PagesID["catalog.courses"]}
              data={dataTable?.output}
              columns={columnsMerged || []}
              configurationTopToolbar={actionsTopToolbar}
              configurationActionForRow={actionForRow?.map((action: any) => {
                const actionKey = action.key || action.url || action.type;
                const withTags = {
                  ...action,
                  idPrefix: "catalog-course-list",
                  testId: `catalog-course-list-${actionKey}-button`,
                  rowIdGetter: (row: any) => row?.id,
                  ariaLabel: action.labelTranslate,
                };
                if (action.type === "popper") {
                  const enrollmentPrefix = "catalog-course-list-enrollment";
                  return {
                    ...withTags,
                    testId: `${enrollmentPrefix}-trigger`,
                    idPrefix: enrollmentPrefix,
                    ariaLabel:
                      t("testAutomation.enrollmentTriggerAria", {
                        defaultValue: "",
                      }) || action.labelTranslate,
                    actions: action.actions?.map((el: any) => {
                      const subKey = el.key || el.url || el.type;
                      const subAria =
                        subKey &&
                        t(`testAutomation.enrollmentActions.${subKey}`, {
                          defaultValue: "",
                        });
                      return {
                        ...el,
                        testId: `${enrollmentPrefix}-${subKey}-button`,
                        idPrefix: enrollmentPrefix,
                        rowIdGetter: (row: any) => row?.id,
                        ariaLabel: subAria || el.labelTranslate,
                      };
                    }),
                  };
                }
                return action.type === "delete"
                  ? { ...withTags, callBack: (id: any) => setIdToDelete(id) }
                  : withTags;
              })}
              totalPages={dataTable?.metadata?.total_elements}
              testIdPrefix="catalog-course-list"
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!idToDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        onCallBackClosed={() => {
          setIdToDelete(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
        routeToSamePage={true}
      />
    </AnimationFadeIn>
  );
};

export default CoursesDigited;
